import { X } from "lucide-react";
import { useEffect, useId, useRef, type FormEvent, type KeyboardEvent, type ReactNode } from "react";
import { t } from "@/i18n";
import { cn } from "@/lib/utils";
import { Button } from "./button";
import { ToastViewport } from "./toast";

/**
 * The one modal shape (iOS `SheetChrome`): bottom sheet on phones, centered
 * dialog on desktop. Native modality makes the background inert; explicit end wrapping keeps
 * Tab out of browser chrome. The entrance animation and body scroll lock live in app.css.
 */
export function Sheet({ id, open, onClose, title, children, footer, size = "md", className }: {
  id?: string; open: boolean; onClose: () => void; title?: ReactNode; children: ReactNode; footer?: ReactNode; size?: "sm" | "md" | "lg"; className?: string;
}) {
  const ref = useRef<HTMLDialogElement>(null);
  const downOnBackdrop = useRef(false);
  const titleId = useId();
  useEffect(() => {
    const d = ref.current;
    if (!d) return;
    const opener = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    if (open && !d.open) d.showModal();
    if (!open && d.open) d.close();
    return () => {
      if (d.open) {
        d.close();
        if (opener?.isConnected) opener.focus();
      }
    };
  }, [open]);

  return (
    <dialog
      ref={ref}
      id={id}
      aria-labelledby={title ? titleId : undefined}
      onCancel={(event) => { event.preventDefault(); onClose(); }}
      onClose={(event) => { if (!event.currentTarget.open) onClose(); }}
      onKeyDown={(event) => {
        if (event.key !== "Tab" || event.altKey || event.ctrlKey || event.metaKey) return;
        const dialog = event.currentTarget;
        if ((event.target as HTMLElement).closest("dialog") !== dialog) return;
        const controls = [...dialog.querySelectorAll<HTMLElement>('button, a[href], input, select, textarea, [tabindex]')]
          .filter((node) => node.tabIndex >= 0 && !node.matches(":disabled") && node.getClientRects().length > 0 && node.closest("dialog") === dialog);
        const first = controls[0], last = controls[controls.length - 1];
        const target = event.shiftKey && document.activeElement === first ? last
          : !event.shiftKey && document.activeElement === last ? first : null;
        if (target) { event.preventDefault(); target.focus(); }
      }}
      // Close on a backdrop click only when the press also started there: selecting text in a field and letting go
      // outside the card must not throw the form away.
      onPointerDown={(e) => { downOnBackdrop.current = e.target === ref.current; }}
      onClick={(e) => { if (e.target === ref.current && downOnBackdrop.current) onClose(); downOnBackdrop.current = false; }}
      className={cn(
        "sheet m-0 w-full max-w-none bg-transparent p-0 backdrop:bg-black/40 backdrop:backdrop-blur-[2px]",
        // Phone: pinned to the bottom. Desktop: centered card.
        "fixed inset-x-0 bottom-0 top-auto lg:inset-0 lg:m-auto lg:h-fit lg:max-h-[90dvh]",
        size === "sm" && "lg:max-w-md", size === "md" && "lg:max-w-lg", size === "lg" && "lg:max-w-3xl",
      )}
    >
      <div className={cn("flex max-h-[92dvh] flex-col rounded-t-[22px] bg-surface text-text shadow-2xl lg:max-h-[90dvh] lg:rounded-[22px]", className)}>
        <div className="relative flex items-center justify-between gap-3 border-b border-border px-5 pb-3 pt-4">
          {/* Drag-handle affordance (phones only). */}
          <div aria-hidden className="absolute left-1/2 top-1.5 h-1 w-10 -translate-x-1/2 rounded-full bg-text/15 lg:hidden" />
          <h2 id={titleId} className="min-w-0 break-words font-display text-lg font-semibold">{title}</h2>
          <Button variant="ghost" size="icon" aria-label={t("common.close")} onClick={onClose}><X className="h-5 w-5" /></Button>
        </div>
        <div className={cn("flex-1 overflow-y-auto px-5 py-4", !footer && "safe-bottom-4 lg:pb-4")}>{children}</div>
        {footer && <div className="flex flex-wrap justify-end gap-2 border-t border-border px-5 py-3 safe-bottom">{footer}</div>}
        {open && <ToastViewport />}
      </div>
    </dialog>
  );
}

/** Sheet + form wiring: Enter submits, Cancel/Save footer, busy state. `submitDisabled` greys the submit out while the form has nothing valid to send. */
export function FormSheet({ open, onClose, onSubmit, title, children, busy, submitLabel, submitDisabled, danger, size }: {
  open: boolean; onClose: () => void; onSubmit: () => unknown; title: ReactNode; children: ReactNode;
  busy?: boolean; submitLabel?: string; submitDisabled?: boolean; danger?: ReactNode; size?: "sm" | "md" | "lg";
}) {
  // One id per sheet, so two form sheets on a page never share a form owner.
  const formId = useId();
  const submit = (e: FormEvent) => { e.preventDefault(); void onSubmit(); };
  // ⌘/Ctrl + Enter saves from anywhere in the form, textareas included (plain Enter there is a new line).
  const submitShortcut = (e: KeyboardEvent<HTMLFormElement>) => {
    if (e.key === "Enter" && (e.metaKey || e.ctrlKey) && !busy && !submitDisabled) { e.preventDefault(); e.currentTarget.requestSubmit(); }
  };
  return (
    <Sheet
      open={open}
      onClose={onClose}
      title={title}
      size={size}
      footer={
        <>
          {danger && <div className="mr-auto">{danger}</div>}
          <Button variant="ghost" onClick={onClose}>{t("common.cancel")}</Button>
          <Button type="submit" form={formId} loading={busy} disabled={submitDisabled}>{submitLabel ?? t("common.save")}</Button>
        </>
      }
    >
      <form id={formId} onSubmit={submit} onKeyDown={submitShortcut} className="flex flex-col gap-4">{children}</form>
    </Sheet>
  );
}

export function ConfirmSheet({ open, onClose, onConfirm, title, body, confirmLabel, busy }: {
  open: boolean; onClose: () => void; onConfirm: () => unknown; title: string; body?: string; confirmLabel?: string; busy?: boolean;
}) {
  return (
    <Sheet open={open} onClose={onClose} title={title} size="sm" footer={
      <>
        <Button variant="ghost" onClick={onClose}>{t("common.cancel")}</Button>
        <Button variant="danger" loading={busy} onClick={() => void onConfirm()}>{confirmLabel ?? t("common.delete")}</Button>
      </>
    }>
      <p className="text-[15px] text-text-secondary">{body ?? t("common.confirmDelete")}</p>
    </Sheet>
  );
}
